import { useEffect } from "react";
import { useFormikContext } from "formik";

/**
 * Component to scroll to the first form error and focus it after submit.
 * Follows RAKBANK accessibility and error handling standards.
 */
// Helper to flatten nested error keys to dot notation
export const flattenErrorKeys = (obj, prefix = "") => {
  if (Array.isArray(obj)) {
    return obj.flatMap((item, idx) => {
      if (typeof item === "object" && item !== null) {
        return flattenErrorKeys(item, `${prefix}${prefix ? "." : ""}${idx}`);
      }
      if (item !== undefined && item !== null) {
        return [`${prefix}${prefix ? "." : ""}${idx}`];
      }
      return [];
    });
  }
  if (typeof obj === "object" && obj !== null) {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      const newKey = prefix ? `${prefix}.${key}` : key;
      if (typeof value === "object" && value !== null) {
        acc.push(...flattenErrorKeys(value, newKey));
      } else if (value !== undefined && value !== null) {
        acc.push(newKey);
      }
      return acc;
    }, []);
  }
  return [];
};

// Converts dot notation with numbers to array notation for Formik fields
export const toFormikFieldName = key => key.replaceAll(/\.((\d)+)\./g, (_match, p1) => `[${p1}].`);
export const sortKeysByDomOrder = (keys, formEl = document) => {
  // Defensive: if formEl is null or doesn't have querySelectorAll, fallback to document
  const el = formEl && typeof formEl.querySelectorAll === "function" ? formEl : document;
  const keyMap = new Map(keys.map(key => [toFormikFieldName(key), key]));
  const domFields = Array.from(
    new Set(
      Array.from(el.querySelectorAll("[name]"))
        .map(field => field.getAttribute("name"))
        .filter(Boolean)
    )
  );
  return domFields.map(name => keyMap.get(name)).filter(Boolean);
};

// Recursively remove undefined values from errors object and arrays
export const cleanErrors = obj => {
  if (Array.isArray(obj)) {
    // Remove undefined items from arrays
    return obj
      .map(item => (typeof item === "object" && item !== null ? cleanErrors(item) : item))
      .filter(item => item !== undefined);
  }
  if (typeof obj === "object" && obj !== null) {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        if (typeof value === "object" && value !== null) {
          const cleaned = cleanErrors(value);
          if (
            Array.isArray(cleaned) ||
            (typeof cleaned === "object" && Object.keys(cleaned).length) ||
            typeof cleaned !== "object"
          ) {
            acc[key] = cleaned;
          }
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});
  }
  return obj;
};

export const ScrollToError = () => {
  const formik = useFormikContext() || {};
  const { errors = {}, isSubmitting = false, isValidating = false } = formik;

  useEffect(() => {
    if (isSubmitting && !isValidating) {
      const cleanedErrors = cleanErrors(errors);
      let keys = flattenErrorKeys(cleanedErrors);
      if (keys.length === 0) return; // No errors, do nothing

      const formEl = document.activeElement?.form;
      keys = sortKeysByDomOrder(keys, formEl);

      if (keys.length > 0) {
        const fieldName = toFormikFieldName(keys[0]);
        const selector = `[name="${fieldName}"]`;
        let errorElement = null;
        if (formEl && typeof formEl.querySelector === "function") {
          errorElement = formEl.querySelector(selector);
        } else {
          errorElement = document.querySelector(selector);
        }
        if (errorElement) {
          const headerOffset = 140;
          const elementTop = errorElement.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({
            top: elementTop - headerOffset,
            behavior: "smooth"
          });
          errorElement.focus({ preventScroll: true });
        }
      }
    }
  }, [errors, isSubmitting, isValidating]);
  return null;
};
